import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdClose } from 'react-icons/md';
import { useLanguage } from '../../context/LanguageContext';

const CreateSupportCaseModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    customer_id: '',
    contract_id: '',
    product_id: '',
    sla_id: '',
    created_by: '',
    source: '',
    case_owner: '',
    subject: '',
    detail: '',
    created_at: new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:MM formatında varsayılan tarih
  });
  
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [products, setProducts] = useState([]);
  const [slaGroups, setSlaGroups] = useState([]);
  const [slaDefinitions, setSlaDefinitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  
  useEffect(() => {
    fetchCustomers();
    fetchUsers();
    fetchAgents();
  }, []);
  
  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };
  
  const fetchContracts = async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contracts`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };
  
  const fetchProducts = async (contractId) => {
    try {
      const response = await api.get(`/contracts/${contractId}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };
  
  const fetchSlaGroups = async (productId) => {
    try {
      const response = await api.get(`/products/${productId}/sla-group`);
      
      // If the product has an SLA group assigned, set it as the only option
      if (response.data && response.data.id) {
        setSlaGroups([response.data]);
        // Auto-select the SLA group since there's only one option
        setFormData(prev => ({ 
          ...prev, 
          sla_group_id: response.data.id 
        }));
        // Fetch SLA definitions for this group
        fetchSlaDefinitions(response.data.id);
      } else {
        setSlaGroups([]);
        setFormData(prev => ({ 
          ...prev, 
          sla_group_id: '',
          sla_id: ''
        }));
        setSlaDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching SLA group for product:', error);
      setSlaGroups([]);
      setFormData(prev => ({ 
        ...prev, 
        sla_group_id: '',
        sla_id: ''
      }));
      setSlaDefinitions([]);
    }
  };
  
  const fetchSlaDefinitions = async (slaGroupId) => {
    try {
      const response = await api.get(`/sla-groups/${slaGroupId}/definitions`);
      setSlaDefinitions(response.data);
    } catch (error) {
      console.error('Error fetching SLA definitions:', error);
      setSlaDefinitions([]);
    }
  };
  
  const fetchUsers = async () => {
    try {
      const response = await api.get('/customer-users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Handle dependent dropdowns
    if (name === 'customer_id') {
      fetchContracts(value);
      setFormData(prev => ({ 
        ...prev, 
        contract_id: '',
        product_id: '',
        sla_group_id: '',
        sla_id: ''
      }));
      setContracts([]);
      setProducts([]);
      setSlaGroups([]);
      setSlaDefinitions([]);
    } else if (name === 'contract_id') {
      fetchProducts(value);
      setFormData(prev => ({ 
        ...prev, 
        product_id: '',
        sla_group_id: '',
        sla_id: ''
      }));
      setProducts([]);
      setSlaGroups([]);
      setSlaDefinitions([]);
    } else if (name === 'product_id') {
      // Fetch SLA group based on product selection
      if (value) {
        fetchSlaGroups(value);
      } else {
        setSlaGroups([]);
        setSlaDefinitions([]);
        setFormData(prev => ({ 
          ...prev, 
          sla_group_id: '',
          sla_id: ''
        }));
      }
    } else if (name === 'sla_group_id') {
      fetchSlaDefinitions(value);
      setFormData(prev => ({ 
        ...prev, 
        sla_id: ''
      }));
      setSlaDefinitions([]);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Log the form data before submission
    
    // Create the support case object with the correct fields
    const supportCase = {
      customer_id: formData.customer_id,
      product_id: formData.product_id,
      sla_id: formData.sla_id,
      created_by: formData.created_by,
      source: formData.source,
      case_owner: formData.case_owner || null,
      subject: formData.subject,
      detail: formData.detail,
      case_status: 'OPEN',
      created_at: formData.created_at // Özel oluşturma tarihini ekle
    };
    
    
    onSubmit(supportCase);
  };
  
  const sourceOptions = [
    { value: 'SMS', label: 'SMS' },
    { value: 'E-Mail', label: 'E-Mail' },
    { value: 'Phone', label: t('supportCase.phone') || 'Phone' },
    { value: 'Social Media', label: 'Social Media' },
    { value: 'Portal', label: 'Portal' },
    { value: 'Other', label: t('supportCase.other') || 'Other' }
  ];
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
      <div className="relative mx-auto w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
        {/* Header - Başlık alanı */}
        <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <h3 className="text-sm sm:text-lg font-medium">{t('supportCase.createSupportCase')}</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <MdClose size={20} />
          </button>
        </div>
        
        <div className="p-3 sm:p-6 lg:p-8 bg-white dark:bg-gray-800">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 sm:gap-4">
            {/* Created Date - Oluşturma tarihi alanı */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.creationDate')}</label>
              <input
                type="datetime-local"
                name="created_at"
                value={formData.created_at}
                onChange={handleChange}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            
            {/* Customer - Müşteri seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.customer')} *</label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('supportCase.customer')} {t('common.select')}</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customer_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Contract - Kontrat seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerCreateSupportModal.contract')} *</label>
              <select
                name="contract_id"
                value={formData.contract_id}
                onChange={handleChange}
                required
                disabled={!formData.customer_id}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('customerCreateSupportModal.contract')} {t('common.select')}</option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contract_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Product - Ürün seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.product')} *</label>
              <select
                name="product_id"
                value={formData.product_id}
                onChange={handleChange}
                required
                disabled={!formData.contract_id}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('supportCase.product')} {t('common.select')}</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* SLA Group - SLA grup seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerCreateSupportModal.slaGroup')} *</label>
              <select
                name="sla_group_id"
                value={formData.sla_group_id}
                onChange={handleChange}
                required
                disabled={!formData.product_id || slaGroups.length === 0}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('customerCreateSupportModal.slaGroup')} {t('common.select')}</option>
                {slaGroups.map(slaGroup => (
                  <option key={slaGroup.id} value={slaGroup.id}>
                    {slaGroup.group_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* SLA Definition (Severity) - SLA önem seviyesi seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.slaSeverity')} *</label>
              <select
                name="sla_id"
                value={formData.sla_id}
                onChange={handleChange}
                required
                disabled={!formData.sla_group_id}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('supportCase.slaSeverity')} {t('common.select')}</option>
                {slaDefinitions.map(definition => (
                  <option key={definition.id} value={definition.id}>
                    {definition.severity}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Created By - Oluşturan kullanıcı seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.createdBy')} *</label>
              <select
                name="created_by"
                value={formData.created_by}
                onChange={handleChange}
                required
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('supportCase.createdBy')} {t('common.select')}</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Source - Kaynak seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.source')} *</label>
              <select
                name="source"
                value={formData.source}
                onChange={handleChange}
                required
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('common.select')}</option>
                {sourceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Assign To - Atama alanı */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.assignedTo')}</label>
              <select
                name="case_owner"
                value={formData.case_owner}
                onChange={handleChange}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">{t('supportCase.notAssignedYet')}</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Subject - Konu alanı */}
            <div className="col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.subject')} *</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            
            {/* Case Detail - Talep detayı alanı */}
            <div className="col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('supportCase.caseDetail')} *</label>
              <textarea
                name="detail"
                value={formData.detail}
                onChange={handleChange}
                required
                rows={2}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            
            {/* Buttons - Butonlar */}
            <div className="col-span-2 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
              >
                {t('supportCase.createSupportCase')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSupportCaseModal; 